import React, { useState } from 'react';
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';

import styled from 'styled-components';
import Routes, { RoutesNames } from '../enums/routes';

import { IRoute, sidebarRoutes } from '../routes/routes';

import { Flex, StyledText } from 'src/components';
import { StyledTextVariantsTypeV2 } from 'src/components/StyledText';

import { BiocerticaDefault } from 'src/assets/images/logos';
import { CloseIconWhite, SidebarBackground, JournalsIconV2, TableUserAvatar } from 'src/assets/images/custom';

import { translate } from '../translations/translations';

import theme from 'src/styles/theme';
import { isNullOrUndefined } from 'src/utils/helpers';
import { toggleSidebar } from 'src/pods/system/state/actions';
import { SidebarDisplayTypes } from 'src/enums/sidebar.display.types';
import Badge from './v2/Badge';
import UserApiCalls from 'src/pods/user/api/ApiCalls';
import NotificationsProvider from 'src/utils/notifications-provider';
import PractitionerCategoryModel from 'src/pods/user/models/PractitionerCategoryModel';
import SessionSidebar from './v2/SessionSidebar';
import { PractitionerStatus } from 'src/enums/practitioner.status';

interface IProps {
    onLogoutClick?: () => void;
    toggle?: boolean;
}

const LogoWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    position: relative;
    padding: 28px 12px;
`;

const SidebarInnerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 20px;
    height: 100%;
    position: relative;
    z-index: 101;
`;

const SidebarHeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;

    #close-sidebar {
        display: none;
    }

    @media only screen and (max-width: 540px) {
        #close-sidebar {
            display: block;
        }
    }
`;

const SidebarContentWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 128px;
    width: calc(100% - 40px);
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    border-bottom: 1px solid rgba(59, 135, 140, 0.08);
`;

const Separator = styled.div`
    width: calc(100% + 40px);
    margin: 12px 0 0 -20px;
    border-bottom: 1px solid rgba(59, 135, 140, 0.08);
`;

const SidebarFooter = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 0;
`;

const ImageWrapper = styled.div`
    img {
        border-radius: 50%;
    }
`;

const SidebarWrapper = styled.div<{ sidebarDisplay?: string }>`
    height: 100%;
    transition: all 0.3s !important;
    position: fixed;
    top: 0;
    left: 0;
    box-sizing: border-box;
    overflow-y: scroll;
    z-index: 1009;
    min-width: 240px;
    width: 288px;
    background: url(${SidebarBackground});
    color: #fff;

    @media only screen and (max-width: 540px) {
        display: ${(props) => (props.sidebarDisplay ? props.sidebarDisplay : 'none')};
    }
`;

const Sidebar = (props: IProps) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.userReducer.user);
    const sessionSidebar = useSelector((state: RootState) => state.sessionReducer.sessionSidebar);
    const sidebarDisplay = useSelector((state: RootState) => state.systemReducer.sidebar);
    const practitioner = useSelector((state: RootState) => state.practitionerReducer.practitioner);
    const warehouseCreated = useSelector((state: RootState) => state.sessionReducer.warehouseCreated);
    const [practitionerCategories, setPractitionerCategories] = useState<PractitionerCategoryModel[]>([]);

    const pathname = window.location.pathname;

    const displayingKitsDisabled = (r: IRoute) => {
        return r.name === RoutesNames.KITS && isNullOrUndefined(practitioner?.warehouseID);
    };

    const closeSidebar = () => {
        dispatch(toggleSidebar(SidebarDisplayTypes.SIDEBAR_DISPLAY_NONE));
    };

    const getPractitionerCategories = async () => {
        try {
            const response = await UserApiCalls.getPractitionersCategories();
            setPractitionerCategories(response.data.results);
        } catch (err) {
            NotificationsProvider.error(translate('common.error'));
        }
    };

    const getPractitionerCategoryById = () => {
        return practitionerCategories.find((category) => category.id === practitioner?.categoryID)?.name;
    };

    const onScreenLoad = () => {
        getPractitionerCategories();
    };

    useEffect(() => {
        onScreenLoad();
        // eslint-disable-next-line
    }, [warehouseCreated]);

    return (
        <SidebarWrapper
            sidebarDisplay={sidebarDisplay}
            style={{ display: pathname === Routes.ONBOARDING ? 'none' : 'block' }}
        >
            <SidebarInnerWrapper>
                <SidebarHeaderWrapper>
                    <img
                        id="close-sidebar"
                        onClick={closeSidebar}
                        src={CloseIconWhite}
                        alt={'close icon'}
                        height="16px"
                        width="16px"
                        style={{ cursor: 'pointer', marginRight: '4px' }}
                    />

                    <LogoWrapper
                        className={'logo-wrapper'}
                        onClick={() => {
                            history.push(Routes.ROOT);
                        }}
                    >
                        <img src={BiocerticaDefault} alt={'biocertica logo'} />
                    </LogoWrapper>
                </SidebarHeaderWrapper>

                <SidebarContentWrapper>
                    {user && (
                        <Flex alignItems="center">
                            <ImageWrapper>
                                <img src={user.profileImage || TableUserAvatar} height="48px" width="48px" alt="user" />
                            </ImageWrapper>
                            <Flex flexDirection="column" margin={['0 12px']}>
                                <StyledText
                                    text={user.firstName + ' ' + user.lastName}
                                    styles={{ marginBottom: '4px' }}
                                />
                                {practitioner && <Badge text={getPractitionerCategoryById()} />}
                            </Flex>
                        </Flex>
                    )}
                    {sessionSidebar ? (
                        <SessionSidebar />
                    ) : practitioner?.status === PractitionerStatus.PracticionerStatusAccepted ? (
                        <>
                            <StyledText
                                variant={StyledTextVariantsTypeV2.PARAGRAPH_UPPERCASE1}
                                text={translate('my.practice')}
                                margin={'24px 0 10px 0'}
                                colorVariant={theme.colors.v2.light.blueSapphire[20]}
                            />
                            {sidebarRoutes.map((r) =>
                                displayingKitsDisabled(r) ? null : (
                                    <NavLink
                                        to={r.path}
                                        activeStyle={{
                                            background: 'rgba(59, 135, 140, 0.24)',
                                            borderRadius: '8px',
                                        }}
                                        style={{
                                            padding: '8px',
                                            textDecoration: 'none',
                                            marginBottom: '8px',
                                        }}
                                        key={r.name}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img src={r.icon} alt="" height="24px" width="24px" />

                                            <StyledText
                                                margin={'0 0 0 10%'}
                                                text={r.name}
                                                variant={StyledTextVariantsTypeV2.NAV_BOLD}
                                                colorVariant={theme.colors.v2.light.darkCyan[60]}
                                                styles={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    </NavLink>
                                ),
                            )}
                        </>
                    ) : (
                        <>
                            <StyledText
                                variant={StyledTextVariantsTypeV2.PARAGRAPH_UPPERCASE1}
                                text={translate('my.practice')}
                                margin={'24px 0 10px 0'}
                                colorVariant={theme.colors.v2.light.blueSapphire[20]}
                            />
                            {sidebarRoutes.map((r) =>
                                displayingKitsDisabled(r) ? null : !r.practitionerAccepted ? (
                                    <NavLink
                                        to={r.path}
                                        activeStyle={{
                                            background: 'rgba(59, 135, 140, 0.24)',
                                            borderRadius: '8px',
                                        }}
                                        style={{
                                            padding: '8px',
                                            textDecoration: 'none',
                                            marginBottom: '8px',
                                        }}
                                        key={r.name}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img src={r.icon} alt="" height="24px" width="24px" />

                                            <StyledText
                                                margin={'0 0 0 10%'}
                                                text={r.name}
                                                variant={StyledTextVariantsTypeV2.NAV_BOLD}
                                                colorVariant={theme.colors.v2.light.darkCyan[60]}
                                                styles={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    </NavLink>
                                ) : (
                                    <NavLink
                                        to={r.path}
                                        activeStyle={{
                                            background: 'rgba(59, 135, 140, 0.24)',
                                            borderRadius: '8px',
                                        }}
                                        onClick={(e) => e.preventDefault()}
                                        style={{
                                            padding: '8px',
                                            textDecoration: 'none',
                                            marginBottom: '8px',
                                            opacity: '0.32',
                                        }}
                                        key={r.name}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img src={r.icon} alt="" height="24px" width="24px" />

                                            <StyledText
                                                margin={'0 0 0 10%'}
                                                text={r.name}
                                                variant={StyledTextVariantsTypeV2.NAV_BOLD}
                                                colorVariant={theme.colors.v2.light.darkCyan[60]}
                                                styles={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    </NavLink>
                                ),
                            )}
                        </>
                    )}

                    {sessionSidebar ? null : (
                        <>
                            <Separator />

                            <StyledText
                                variant={StyledTextVariantsTypeV2.PARAGRAPH_UPPERCASE1}
                                text={translate('other')}
                                margin={'24px 0 10px 0'}
                                colorVariant={theme.colors.v2.light.blueSapphire[20]}
                            />
                            <NavLink
                                to={Routes.JOURNALS}
                                activeStyle={{
                                    background: 'rgba(59, 135, 140, 0.24)',
                                    borderRadius: '8px',
                                }}
                                style={{ padding: '5% 8px', textDecoration: 'none', marginBottom: '48px' }}
                                key={'Journals'}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img src={JournalsIconV2} alt="" height="24px" width="24px" />

                                    <StyledText
                                        margin={'0 0 0 10%'}
                                        text={'Journals'}
                                        variant={StyledTextVariantsTypeV2.NAV_BOLD}
                                        colorVariant={theme.colors.v2.light.darkCyan[60]}
                                        styles={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </NavLink>

                            <Separator />
                        </>
                    )}
                    <SidebarFooter>
                        <StyledText
                            onClick={props.onLogoutClick}
                            text={translate('common.logout')}
                            colorVariant={theme.colors.v2.light.englishVermillion.default}
                            variant={StyledTextVariantsTypeV2.NAV_BOLD}
                            margin="0 0 20px 0"
                            styles={{ cursor: 'pointer' }}
                        />
                        <StyledText
                            text={translate('v2.contactUs')}
                            colorVariant={theme.colors.v2.light.blueSapphire[30]}
                            variant={StyledTextVariantsTypeV2.NAV_BOLD}
                            margin="0 0 20px 0"
                            styles={{ cursor: 'pointer' }}
                            onClick={() => window.open('https://biocertica.com/pages/contact-us', '_blank')}
                        />
                        <a
                            href="https://biocertica-pdf.s3.af-south-1.amazonaws.com/4d2831dc-3be1-444c-a88e-c58499e0cdfb.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                textDecoration: 'none',
                                marginBottom: '20px',
                                color: theme.colors.v2.light.blueSapphire[30],
                                cursor: 'pointer',
                                fontFamily: theme.typography.bold,
                                fontSize: 15,
                                letterSpacing: -0.08,
                            }}
                        >
                            {translate('v2.privacyPolicy')}
                        </a>

                        <StyledText
                            onClick={() => history.push(Routes.TERMS_AND_CONDITIONS)}
                            text={translate('v2.termsConditions')}
                            colorVariant={theme.colors.v2.light.blueSapphire[30]}
                            variant={StyledTextVariantsTypeV2.NAV_BOLD}
                            margin="0 0 20px 0"
                            styles={{ cursor: 'pointer' }}
                        />

                        <StyledText
                            text={translate('biocertica.copyright')}
                            colorVariant={theme.colors.v2.light.blueSapphire[30]}
                            variant={StyledTextVariantsTypeV2.PARAGRAPH_4}
                            margin="8px 12px"
                            styles={{ cursor: 'pointer' }}
                        />
                    </SidebarFooter>
                </SidebarContentWrapper>
            </SidebarInnerWrapper>
        </SidebarWrapper>
    );
};

export default Sidebar;
